import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Button, Typography, TextField, Switch, Select } from "@packages/ui";
import { Box, Grid, Link } from "@material-ui/core";
import InputMask from "react-input-mask";
import _ from "lodash";
import SnackBar from "../../layouts/SnackBar";
import * as authActions from "src/actions/authActions";
import * as appActions from "src/actions/appActions";
import getCountryFromURL from "src/utils/getCountryFromUrl";
// const { auLocations, nzLocations } = require("../../../public/static/json/locations.json");
const { auLocations, nzLocations } = require("@myjourney-assets/json/locations.json");
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as checkinActions from "src/actions/checkinActions";
import getBrandFromUrl from "src/utils/getBrandFromUrl";

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [corpSwitch, setCorpSwitch] = React.useState(false);
  const authLoading = useSelector((state) => state.auth.loading);
  const rentalLoading = useSelector((state) => state.app.loading);
  const auth = useSelector((state) => ({ ...state.auth }));
  const rentals = useSelector((state) => ({ ...state.app.rentals }));
  const urlParams = new URLSearchParams(window.location.search);
  const filteredLocations = useSelector((state) => {
    const locations = state.checkin.brandLocations || [];
    const uniqueLocationsMap = new Map();
    locations.forEach((loc) => {
      if (!uniqueLocationsMap.has(loc.locationName)) {
        uniqueLocationsMap.set(loc.locationName, loc);
      }
    });
    return Array.from(uniqueLocationsMap.values()).sort((a, b) =>
      a.locationName.localeCompare(b.locationName)
    );
  });

  const paramRaNumber = urlParams.get("raNumber") ? urlParams.get("raNumber") : "";
  const paramLastName = urlParams.get("lastName") ? urlParams.get("lastName") : "";
  const paramAccountType = urlParams.get("accountType") ? urlParams.get("accountType") : "";
  const [raFlag, setRaFlag] = useState(true);
  const country = getCountryFromURL();
  const brand = getBrandFromUrl();

  useEffect(() => {
    // Set corporate switch based on account type
    setCorpSwitch(paramAccountType === "corporate");
    const brandMap = {
      hertz: "ZE",
      dollar: "ZR",
      thrifty: "ZT",
      firefly: "FF",
      ace: "AC",
    };
    dispatch(
      checkinActions.getBrandLocations({ brand: brandMap[brand.toLowerCase()], country: country })
    );
  }, []);

  useEffect(() => {
    if (!_.isEmpty(rentals)) {
      navigate("/app/rentalhistory", { replace: true });
    }
  }, [rentals]);

  useEffect(() => {
    if (auth.success && _.isEmpty(rentals)) {
      dispatch(appActions.getRentals(auth));
    }
  }, [auth.success]);

  const filterInputs = (formSubmitData) => {
    return Object.entries(formSubmitData).reduce((result, [key, value]) => {
      if (!value) {
        return result;
      }

      if (key === "dob") {
        value = value.replace(/ \/ /g, "");
      }

      if (key === "pickupDate") {
        value = value.split(/ \/ /g).reverse().join("-");
      }

      if (key === "raNumber") {
        const valueString = value.toString();
        value = valueString.length == 9 ? valueString : valueString + (valueString % 7);
      }

      result[key] = value.toString().trim();
      return result;
    }, {});
  };

  const formik = useFormik({
    initialValues: {
      raNumber: paramRaNumber,
      dob: "",
      lastName: paramLastName,
      cdp: "",
      pickupDate: "",
      pickupLoc: "",
      driverLicenseNumber: "",
    },
    onSubmit: (values) => {
      const params = filterInputs(values);
      params.country = country;
      console.log("params", params);
      dispatch(
        authActions.login({
          values: params,
          corporate: corpSwitch,
        })
      );
    },
    validate: (values) => {
      const errors = {};
      if (raFlag && !values.raNumber) {
        errors.raNumber = "Required";
      } else if (raFlag && !/[0-9]{8}/g.test(values.raNumber)) {
        errors.raNumber = "Please enter a valid Rental Agreement Number";
      }
      if (!corpSwitch && raFlag) {
        if (!values.dob) {
          errors.dob = "Required";
        } else {
          // Remove spaces for validation
          const cleanDob = values.dob.replace(/\s/g, "");

          // Check if it matches the format DD/MM/YYYY
          if (!/^([0-2][0-9]|(3)[0-1])\/(((0)[0-9])|((1)[0-2]))\/\d{4}$/g.test(cleanDob)) {
            errors.dob = "Please enter a valid date (dd/mm/yyyy)";
          } else {
            // Additional check: year must be 4 digits
            const yearPart = cleanDob.split("/")[2];
            if (yearPart.length !== 4) {
              errors.dob = "Year must be 4 digits";
            }
          }
        }
      }
      if ((corpSwitch && !values.lastName) || (!raFlag && !values.lastName)) {
        errors.lastName = "Required";
      }
      if (!raFlag && !values.pickupDate) {
        errors.pickupDate = "Required";
      } else if (!raFlag && values.pickupDate) {
        const cleanPickupDate = values.pickupDate.replace(/\s/g, "");

        if (!/^([0-2][0-9]|(3)[0-1])\/(((0)[0-9])|((1)[0-2]))\/\d{4}$/g.test(cleanPickupDate)) {
          errors.pickupDate = "Please enter a valid date (dd/mm/yyyy)";
        } else {
          const yearPart = cleanPickupDate.split("/")[2];
          if (yearPart.length !== 4) {
            errors.pickupDate = "Year must be 4 digits";
          }
        }
      }
      if (!raFlag && !values.pickupLoc) {
        errors.pickupLoc = "Required";
      }
      if (!raFlag && !values.driverLicenseNumber) {
        errors.driverLicenseNumber = "Required";
      }
      if (corpSwitch && !values.cdp) {
        errors.cdp = "Required";
      } else if (corpSwitch && !/[0-9]*/g.test(values.cdp)) {
        errors.cdp = "Please enter a valid CDP";
      } else if (corpSwitch && values.cdp.length < 4) {
        errors.cdp = "The minimum length of the last digits or CDP must be 4 characters";
      } else if (corpSwitch && values.cdp.length > 10) {
        errors.cdp = "The maximum length of the CDP is 10 characters";
      }
      return errors;
    },
  });

  return (
    <React.Fragment>
      <SnackBar />
      <form onSubmit={formik.handleSubmit}>
        {raFlag ? (
          <>
            <Box style={{ fontSize: "0.8rem", display: "flex", justifyContent: "end" }}>
              <Typography
                style={{ color: "#a0a0a0", fontSize: "0.9rem" }}
              >{`Don't have your rental number?`}</Typography>
              <Typography>&nbsp;</Typography>
              <Link
                underline="always"
                component={"button"}
                style={{
                  fontWeight: "bold",
                }}
                onClick={() => {
                  formik.resetForm();
                  setRaFlag(false);
                }}
              >
                {` Search here.`}
              </Link>
            </Box>
            <TextField
              fullWidth
              name="raNumber"
              label="Rental Agreement"
              error={formik.errors.raNumber ? true : false}
              helperText={formik.errors.raNumber ? formik.errors.raNumber : null}
              onChange={formik.handleChange}
              value={formik.values.raNumber}
              inputProps={{ type: "number" }}
              onInput={(e) => {
                e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 9);
              }}
            />
            {!corpSwitch ? (
              <InputMask
                mask="99 / 99 / 9999"
                value={formik.values.dob}
                onChange={formik.handleChange}
                maskChar=" "
                label="Date Of Birth (dd/mm/yyyy)"
              >
                {() => (
                  <TextField
                    fullWidth
                    id="dob"
                    name="dob"
                    placeholder={"Date of Birth (dd/mm/yyyy)"}
                    label={"Date of Birth (dd/mm/yyyy)"}
                    style={{ marginTop: 15, padding: 0 }}
                    error={formik.errors.dob ? true : false}
                    helperText={formik.errors.dob ? formik.errors.dob : null}
                    inputProps={{ inputMode: "numeric" }}
                  />
                )}
              </InputMask>
            ) : (
              <div>
                <TextField
                  fullWidth
                  onChange={formik.handleChange}
                  label="Last Name"
                  name="lastName"
                  error={formik.errors.lastName ? true : false}
                  value={formik.values.lastName}
                  helperText={formik.errors.lastName ? formik.errors.lastName : null}
                />
                <TextField
                  fullWidth
                  onChange={formik.handleChange}
                  label="CDP or Last 4 Digits Of Credit Card"
                  name="cdp"
                  error={formik.errors.cdp ? true : false}
                  helperText={formik.errors.cdp ? formik.errors.cdp : null}
                  inputProps={{ type: "text", inputmode: "numeric", maxlength: "10" }}
                  onInput={(e) => {
                    e.target.value = e.target.value.toString().slice(0, 10);
                  }}
                />
              </div>
            )}
            <Grid container align-items="flex-end" justify="flex-end">
              <Typography style={{ marginTop: 4 }}>Sign in with a corporate account?</Typography>
              <Switch
                checked={corpSwitch}
                onChange={() => {
                  setCorpSwitch((corpSwitch) => !corpSwitch);
                  if (corpSwitch) {
                    formik.setFieldValue("dob", "");
                  } else {
                    formik.setFieldValue("lastName", "");
                    formik.setFieldValue("cdp", "");
                  }
                }}
              />
            </Grid>
            <Box style={{ marginTop: 15 }}>
              <Button
                fullWidth
                type="submit"
                loading={authLoading || rentalLoading}
                title={"Sign In"}
              />
            </Box>
          </>
        ) : (
          <>
            <Box style={{ fontSize: "0.8rem", display: "flex", justifyContent: "end" }}>
              <Typography>&nbsp;</Typography>
              <Link
                underline="always"
                component={"button"}
                style={{
                  fontWeight: "bold",
                }}
                onClick={() => {
                  formik.resetForm();
                  setRaFlag(true);
                }}
              >
                {` Return to main page.`}
              </Link>
            </Box>
            <TextField
              fullWidth
              onChange={formik.handleChange}
              label="Last Name"
              name="lastName"
              error={formik.errors.lastName ? true : false}
              value={formik.values.lastName}
              helperText={formik.errors.lastName ? formik.errors.lastName : null}
            />
            <TextField
              fullWidth
              onChange={formik.handleChange}
              label="Driver License Number"
              name="driverLicenseNumber"
              error={formik.errors.driverLicenseNumber ? true : false}
              value={formik.values.driverLicenseNumber}
              helperText={
                formik.errors.driverLicenseNumber ? formik.errors.driverLicenseNumber : null
              }
            />
            <InputMask
              mask="99 / 99 / 9999"
              value={formik.values.pickupDate}
              onChange={formik.handleChange}
              maskChar=" "
              label="Pick up Date (dd/mm/yyyy)"
            >
              {() => (
                <TextField
                  fullWidth
                  id="pickupDate"
                  name="pickupDate"
                  placeholder={"Pick up Date (dd/mm/yyyy)"}
                  label={"Pick up Date (dd/mm/yyyy)"}
                  style={{ marginTop: 15, padding: 0 }}
                  error={formik.errors.pickupDate ? true : false}
                  helperText={formik.errors.pickupDate ? formik.errors.pickupDate : null}
                  inputProps={{ inputMode: "numeric" }}
                />
              )}
            </InputMask>
            <Autocomplete
              label="pickupLoc"
              id="pickupLoc"
              name="pickupLoc"
              disableClearable
              error={formik.errors.pickupLoc ? true : false}
              options={filteredLocations}
              getOptionLabel={(option) => option?.locationName || ""}
              renderOption={(option) => option.locationName}
              getOptionSelected={(option, value) => option.locationName == value.locationName}
              renderInput={(params) => (
                <TextField
                  inputProps={{ inputMode: "string" }}
                  {...params}
                  fullWidth
                  value={formik.values.pickupLoc}
                  label="Pickup Location"
                  placeholder={"Pickup Location"}
                  error={formik.errors.pickupLoc ? true : false}
                  helperText={formik.errors.pickupLoc ? formik.errors.pickupLoc : null}
                />
              )}
              onChange={(e, data) => formik.setFieldValue("pickupLoc", data.locationCode)}
            />
            <Box style={{ marginTop: 15 }}>
              <Button
                fullWidth
                type="submit"
                loading={authLoading || rentalLoading}
                title={"Sign In"}
              />
            </Box>
          </>
        )}
      </form>
    </React.Fragment>
  );
};

export default LoginForm;
